import { computed, ComputedRef, reactive } from "vue";
import axios from "axios";

import useTranslation from "@Composables/useTranslation";
import { ProcessServerErrors } from "@Modules/shared/Api/ServerErrors";

/**
 * Options for bulk CSV upload
 */
export type BulkUploadOptions = {
    orgID: string;
    teamID: string;
    domain: string;
    withHeader: boolean;
};

/**
 * Response from bulk CSV upload
 */
export type BulkUploadResponse =
    | { success: true; total: number }
    | { success: false };

export type BulkUploadApi = {
    file: ComputedRef<File | null>;
    errors: ComputedRef<Record<string, string>>;
    loading: ComputedRef<boolean>;
    validateAndSetFile: (file: File) => void;
    clearFile: () => void;
    clearErrors: () => void;
    uploadTeamBulkCsv: (options: BulkUploadOptions) => Promise<BulkUploadResponse>;
    setRouter: (router: Pick<HttpInterface, "post">) => BulkUploadApi;
};

export default function BulkUploadApi(): BulkUploadApi {
    const { __ } = useTranslation();

    const state = reactive<{
        file: File | null;
        errors: Record<string, string>;
        loading: boolean;
    }>({
        file: null,
        errors: {},
        loading: false,
    });

    let router: Pick<HttpInterface, "post"> = axios;

    return {
        file: computed(() => state.file),
        errors: computed(() => state.errors),
        loading: computed(() => state.loading),

        /**
         * Validates file size and stores the file reference
         */
        validateAndSetFile(file: File): void {
            const megabyte = 1024 * 1024;
            const maxSize = megabyte * 10;

            state.errors = {};

            if (file.size > maxSize) {
                state.errors.file = __("shortener.bulk_upload_file_size_exceeds", { size: "10MB" }) || "File size exceeds 10MB";
                state.file = null;
            } else {
                state.file = file;
            }
        },

        /**
         * Clears the file reference
         */
        clearFile(): void {
            state.file = null;
        },

        /**
         * Clears all errors
         */
        clearErrors(): void {
            state.errors = {};
        },

        /**
         * Uploads a CSV file for bulk URL creation for a team
         */
        async uploadTeamBulkCsv(options: BulkUploadOptions): Promise<BulkUploadResponse> {
            const { orgID, teamID, domain, withHeader } = options;

            if (!state.file) {
                state.errors.file = "No file selected";
                return { success: false };
            }

            const formData = new FormData();
            formData.append("file", state.file);
            formData.append("domain", domain);
            formData.append("withHeader", String(withHeader));

            state.loading = true;
            state.errors = {};

            try {
                const requestUrl = `/app/api/organization/${orgID}/team/${teamID}/bulk/csv`;
                const response = await router.post<{ total: number }>(requestUrl, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });

                return {
                    success: true,
                    total: response.data?.total,
                };
            } catch (error: unknown) {
                if (error && typeof error === "object" && "response" in error) {
                    const axiosError = error as { response?: { data?: { errors?: Record<string, string[]> } } };
                    if (axiosError.response?.data?.errors) {
                        state.errors = ProcessServerErrors(axiosError.response.data.errors);
                    }
                }

                return { success: false };
            } finally {
                state.loading = false;
            }
        },

        /**
         * Allows you to swap out the router for a fake one when writing tests
         */
        setRouter(newRouter) {
            router = newRouter;
            return this;
        },
    };
}
